// Hand-rolled fetch wrappers for the /labels endpoints.

import { getImpersonationHeaders } from "./impersonation";

export interface Label {
  id: string;
  name: string;
  color: string;
  taskCount: number;
  taskIds: string[];
  orphanTaskCount: number;
}

export interface SimpleLabel {
  id: string;
  name: string;
  color: string;
}

export interface BatchAnalyzeResult {
  ok: boolean;
  analysed: number;
  tasksWithLabels: number;
  newLabelsCreated: number;
  reason?:
    | "ok"
    | "no_tasks"
    | "ai_cap_reached"
    | "ai_failed"
    | "already_running";
}

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, {
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...getImpersonationHeaders(),
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try {
      const body = await r.json();
      if (typeof body?.error === "string") msg = body.error;
      else if (typeof body?.message === "string") msg = body.message;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return (await r.json()) as T;
}

export const listLabels = () =>
  jsonFetch<{ labels: Label[]; palette: string[] }>("/api/labels");

export const createLabel = (body: { name: string; color?: string }) =>
  jsonFetch<Label>("/api/labels", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const renameLabel = (
  id: string,
  body: { name?: string; color?: string },
) =>
  jsonFetch<{ id: string; name: string; color: string }>(`/api/labels/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export const deleteLabel = (id: string) =>
  jsonFetch<{ ok: boolean }>(`/api/labels/${id}`, { method: "DELETE" });

export const setTaskLabels = (taskId: string, labelIds: string[]) =>
  jsonFetch<{ ok: boolean; labels: SimpleLabel[] }>(
    `/api/tasks/${taskId}/labels`,
    {
      method: "POST",
      body: JSON.stringify({ labelIds }),
    },
  );

export const removeTaskLabel = (taskId: string, labelId: string) =>
  jsonFetch<{ ok: boolean }>(`/api/tasks/${taskId}/labels/${labelId}`, {
    method: "DELETE",
  });

export const fetchTaskLabels = (taskIds: string[]) => {
  if (taskIds.length === 0) return Promise.resolve({ byTaskId: {} });
  const qs = encodeURIComponent(taskIds.join(","));
  return jsonFetch<{ byTaskId: Record<string, SimpleLabel[]> }>(
    `/api/task-labels?taskIds=${qs}`,
  );
};

export const batchAnalyzeLabels = () =>
  jsonFetch<BatchAnalyzeResult>("/api/labels/batch-analyze", {
    method: "POST",
    body: "{}",
  });

export const promoteLabelToProject = (id: string) =>
  jsonFetch<{
    ok: boolean;
    project: { id: string; name: string };
    attachedTaskCount: number;
  }>(`/api/labels/${id}/promote-to-project`, {
    method: "POST",
    body: "{}",
  });

export const clearAiTags = () =>
  jsonFetch<{ ok: boolean }>("/api/labels/clear-ai-tags", {
    method: "POST",
    body: "{}",
  });
